/**
 * AssignmentDetailDrawer.tsx
 * Right-side detail panel for an employee assignment bar (UX overhaul).
 * Shows the task/links, lets you reassign to a capable peer (via the
 * balance-assistant candidates), and delete. Reassign + dialogs are
 * orchestrated by the page (so skill-gate / conflict prompts can appear).
 *
 * Wave 3 (percent retirement): the commit-on-blur "Allocation %" field is
 * GONE — an assignment is presence (busy on its covered days); the server
 * pins Percent=100. Reassign candidates show percent-of-week free (Q5B).
 */

import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, Button, MenuItem,
  Divider, Chip, Link, CircularProgress, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';
import { EmployeeAvatar } from '../../pages/schedule/views/skill-matrix/EmployeeAvatar';
import { SkillLevelChip } from './SkillLevelChip';
import { employeeBarColor } from './employeeScheduleShared';
import {
  fetchReassignCandidates, type EmployeeBar, type EmployeeBarUser, type ReassignCandidate,
} from '../../services/scheduleEmployeeBars';

export interface AssignmentDetailDrawerProps {
  open: boolean;
  bar: EmployeeBar | null;
  user: EmployeeBarUser | null;
  readOnly?: boolean;
  onClose: () => void;
  onReassign: (bar: EmployeeBar, targetUserKey: string) => void;
  onDelete: (bar: EmployeeBar) => void;
}

export const AssignmentDetailDrawer: React.FC<AssignmentDetailDrawerProps> = ({
  open, bar, user, readOnly, onClose, onReassign, onDelete,
}) => {
  const theme = useTheme();
  const [candidates, setCandidates] = useState<ReassignCandidate[]>([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    setConfirmDel(false);
    if (open && bar?.scheduleTaskKey) {
      setLoadingCand(true);
      fetchReassignCandidates(bar.assignmentKey)
        .then((r) => setCandidates(r.candidates))
        .catch(() => setCandidates([]))
        .finally(() => setLoadingCand(false));
    } else {
      setCandidates([]);
    }
  }, [open, bar]);

  const woLink = bar?.workOrderKey
    ? (bar.projectId ? `/projects/${bar.projectId}/workorders/${bar.workOrderKey}` : `/workorders/${bar.workOrderKey}`)
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
      PaperProps={{ sx: { width: 360, maxWidth: '92vw' } }}
    >
      {bar && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* header */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: employeeBarColor(theme, bar.kind), mr: 1 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{bar.name}</Typography>
              <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            {user && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmployeeAvatar displayName={user.displayName} email={user.email} photoUrl={user.photoUrl} jobClass={user.jobClass} size={28} />
              </Box>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip size="small" label={bar.status} />
              <Chip size="small" label={bar.kind} />
              {/* RM-4: working-day attendance of a 24/7 test (full duration —
                  lab answer Q2, 2026-07-10). */}
              {bar.kind === 'Task' && bar.runMode === 'continuous' && (
                <Chip size="small" label="24/7 test" sx={{ backgroundColor: theme.schedule.continuous, color: '#fff' }} />
              )}
              {bar.overbooked && <Chip size="small" label="Assigned on a day off" sx={{ backgroundColor: theme.schedule.heatmapHigh, color: '#fff' }} />}
            </Box>
          </Box>

          {/* body */}
          <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
            <Typography variant="caption" color="text.secondary">Schedule</Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>{bar.startDate} → {bar.endDate} (week of {bar.weekStart})</Typography>

            {bar.dayLocked && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Day position is fixed by the machine booking{bar.machineCode ? ` (${bar.machineCode})` : ''}.
              </Typography>
            )}

            {/* links */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
              {bar.quoteKey && <Link component={RouterLink} to={`/quotes/${bar.quoteKey}`} variant="body2">Open Quote {bar.quoteNumber ?? ''}</Link>}
              {woLink && <Link component={RouterLink} to={woLink} variant="body2">Open Work Order {bar.workOrderNo ?? ''}</Link>}
            </Box>

            {/* reassign */}
            {bar.scheduleTaskKey && !readOnly && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">Reassign to</Typography>
                {loadingCand ? (
                  <Box sx={{ py: 1 }}><CircularProgress size={16} /></Box>
                ) : (
                  <TextField select size="small" fullWidth value="" onChange={(e) => { if (e.target.value) onReassign(bar, e.target.value); }} sx={{ mt: 0.5 }}>
                    <MenuItem value="" disabled>Select a person…</MenuItem>
                    {candidates.map((c) => (
                      <MenuItem key={c.userKey} value={c.userKey}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span style={{ flex: 1 }}>{c.displayName}</span>
                          <SkillLevelChip level={c.skillLevel} pixelSize={18} />
                          {/* Q5B: freeCapacity = percent of the WEEK's days still free. */}
                          <Chip size="small" label={`${c.freeCapacity}% of week free`} sx={{ height: 18, fontSize: '0.6rem' }} color={c.fitsWithoutOverbook ? 'success' : 'default'} />
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}
          </Box>

          {/* footer */}
          {!readOnly && (
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              {confirmDel ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button fullWidth size="small" color="error" variant="contained" onClick={() => { onDelete(bar); onClose(); }}>Confirm delete</Button>
                  <Button fullWidth size="small" onClick={() => setConfirmDel(false)}>Cancel</Button>
                </Box>
              ) : (
                <Button fullWidth size="small" color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmDel(true)}>Delete assignment</Button>
              )}
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};
